import { ExerciseDto, ExerciseSourceDtoTypeEnum } from '../dto';

export const EXERCISE_THUMBNAIL_DEFAULT = 'assets/images/khero-thumb-default.png';

export function findExerciseThumbnailUrl(exercise?: ExerciseDto | null): string | undefined {
  const source = exercise?.sources?.find(item => item.type === ExerciseSourceDtoTypeEnum.THUMBNAIL);
  return source?.url?.path;
}

export function getExerciseThumbnailPath(exercise?: ExerciseDto | null): string {
  return findExerciseThumbnailUrl(exercise) ?? EXERCISE_THUMBNAIL_DEFAULT;
}

export function hasExerciseThumbnail(exercise?: ExerciseDto | null): boolean {
  return !!findExerciseThumbnailUrl(exercise);
}

/**
 * Swaps an `<img>` whose URL failed to load (a 404, a moved media file) to the
 * branded default asset. Bound to `(error)`, so it must be idempotent: the
 * default itself failing would otherwise re-enter forever.
 */
export function fallbackToDefaultThumbnail(event: Event): void {
  const img = event.target as HTMLImageElement | null;
  if (!img || img.src.endsWith(EXERCISE_THUMBNAIL_DEFAULT)) return;
  img.src = EXERCISE_THUMBNAIL_DEFAULT;
}

/** Schemes that must never be trusted into an iframe `src` (XSS vectors). */
const UNSAFE_URL_SCHEME = /^\s*(javascript|data|vbscript):/i;

/** True when the URL is safe to embed as an iframe resource (no dangerous scheme). */
export function isSafeEmbedUrl(url?: string | null): boolean {
  return !!url && !UNSAFE_URL_SCHEME.test(url);
}

export function findExerciseVideoUrl(exercise?: ExerciseDto | null): string | undefined {
  const source = exercise?.sources?.find(item => item.type === ExerciseSourceDtoTypeEnum.VIDEO);
  return source?.url?.path;
}

/**
 * The exercise's video URL when it can actually be handed to the player, else
 * null. Every video affordance in the app goes through here: a URL that fails
 * the scheme guard must not produce a play button at all, because the player
 * would silently refuse to open it.
 *
 * A video is a file on our own infrastructure, served statically with Range
 * support — so `url.path` goes straight into a `<video src>` and seeking works.
 * It used to be a Vimeo reference whose player URL was composed from the
 * source's `name`; there is no such composition anywhere any more.
 */
export function getEmbeddableExerciseVideoUrl(exercise?: ExerciseDto | null): string | null {
  const url = findExerciseVideoUrl(exercise);
  return isSafeEmbedUrl(url) ? url! : null;
}

export function hasExerciseVideo(exercise?: ExerciseDto | null): boolean {
  return !!getEmbeddableExerciseVideoUrl(exercise);
}
